import { isPlatformServer } from '@angular/common';
import {
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { isDomesticAndGeneralApi } from '@common';
import { ReCaptchaV3Service } from 'ng-recaptcha-2';
import { mergeMap, Observable } from 'rxjs';
import { BYPASS_RECAPTCHA_INTERCEPTOR } from '../../constants/bypass-recaptcha-interceptor.constant';
import { BYPASS_RECAPTCHA_SESSION } from '../../constants/bypass-recaptcha-session.constant';
import { AuthService } from '../../services/auth/auth.service';
import { AUTH_CONFIG } from '../../tokens/auth-config/auth-config.token';
import { RECAPTCHA_OVERRIDE_TOKEN } from '../../tokens/recaptcha-override/recaptcha-override.token';

export const recaptchaTokenInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const authConfig = inject(AUTH_CONFIG);
  const authService = inject(AuthService);

  const platformId = inject(PLATFORM_ID);
  const recaptchaV3Service = inject(ReCaptchaV3Service);
  const recaptchaOverrideToken = inject(RECAPTCHA_OVERRIDE_TOKEN, {
    optional: true,
  });

  if (
    !isDomesticAndGeneralApi(request.url) ||
    request.context.get(BYPASS_RECAPTCHA_INTERCEPTOR)
  ) {
    return next(request);
  }

  if (recaptchaOverrideToken) {
    const updatedRequest = request.clone({
      headers: request.headers.set('x-recaptcha-token', recaptchaOverrideToken),
    });
    return next(updatedRequest);
  }

  if (isPlatformServer(platformId)) {
    return next(request);
  }

  if (
    authConfig.recaptchaSession &&
    !request.context.get(BYPASS_RECAPTCHA_SESSION)
  ) {
    const updatedRequest = request.clone({
      withCredentials: true,
    });

    return authService.recaptchaSession$.pipe(
      mergeMap(() => next(updatedRequest)),
    );
  }

  return recaptchaV3Service.execute('getToken').pipe(
    mergeMap((token: string) => {
      const updatedRequest = request.clone({
        headers: request.headers.set('x-recaptcha-token', token),
      });

      return next(updatedRequest);
    }),
  );
};
